import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '../../components/ui/tooltip';

// icons
import { 
  Add, 
  Eye, 
  SearchNormal1, 
  Global, 
  Sms, 
  Calendar, 
  TickCircle, 
  CloseCircle, 
  Timer, 
  Refresh2
} from 'iconsax-react';

// project imports
import { getTenants } from '../../services/tenantApi';
import { Loader } from '../../components/ui/loader';

// ==============================|| AGENCY LIST ||============================== //

const TenantList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination State (0-indexed to match UserManagement.js)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, active: 0, suspended: 0, trial: 0 });

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchTenants();
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  // Reset page to 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1, // backend is 1-indexed
        limit: rowsPerPage,
        search: debouncedSearch,
        status: statusFilter
      };
      const response = await getTenants(params);
      setTenants(response.data || []);
      
      if (response.meta) {
        setTotalItems(response.meta.totalCount || 0);
        if (response.meta.stats) {
          setStats(response.meta.stats);
        }
      } else {
        setTotalItems(response.data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setTenants([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Compute metrics from backend stats
  const totalAgencies = stats.total;
  const activeAgencies = stats.active;
  const suspendedAgencies = stats.suspended;
  const trialAgencies = stats.trial;

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return (
          <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200/50 flex items-center gap-1.5 w-fit mx-auto px-2.5 py-0.5 font-medium rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Active
          </Badge>
        );
      case 'suspended':
        return (
          <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50 dark:bg-rose-950/20 dark:text-rose-400 border border-rose-200/50 flex items-center gap-1.5 w-fit mx-auto px-2.5 py-0.5 font-medium rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            Suspended
          </Badge>
        );
      case 'trial':
        return (
          <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200/50 flex items-center gap-1.5 w-fit mx-auto px-2.5 py-0.5 font-medium rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Trial
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-50 text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-400 border border-slate-200/50 flex items-center gap-1.5 w-fit mx-auto px-2.5 py-0.5 font-medium rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            {status || 'Unknown'}
          </Badge>
        );
    }
  };

  const paginatedTenants = tenants; // Backend already paginates

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('all');
  };

  const isFiltered = search !== '' || statusFilter !== 'all';



  return (
    <div className="space-y-6">
      {/* Header section (Matches UserManagement.js) */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Healthcare Agencies
        </h1>
        <p className="text-sm text-muted-foreground">
          Monitor, search, and onboard system tenants and administrative contacts
        </p>
      </div>

      {/* Stats Cards (Matches UserManagement.js layout and colors) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-primary">
                  {loading ? '...' : totalAgencies}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Total Agencies
                </p>
              </div>
              <div className="p-2 bg-primary/10 rounded-lg">
                <Global size={24} className="text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">
                  {loading ? '...' : activeAgencies}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Active
                </p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TickCircle size={24} className="text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-rose-600">
                  {loading ? '...' : suspendedAgencies}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Suspended
                </p>
              </div>
              <div className="p-2 bg-rose-100 rounded-lg">
                <CloseCircle size={24} className="text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-amber-600">
                  {loading ? '...' : trialAgencies}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Trial
                </p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Timer size={24} className="text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters (Matches UserManagement.js layout) */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-1/3">
              <div className="relative w-full">
                <SearchNormal1 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type="text"
                  placeholder="Search by name, domain, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-full bg-background"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto items-center">
              <div className="w-full sm:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isFiltered && (
                <Button
                  variant="ghost"
                  onClick={handleClearFilters}
                  className="w-full sm:w-auto text-muted-foreground hover:text-foreground text-sm flex items-center gap-1.5 h-10 px-3"
                >
                  <Refresh2 size={16} />
                  Clear Filters
                </Button>
              )}

              {hasPermission('client.create') && (
                <Button
                  onClick={() => navigate('/tenants/create')}
                  className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
                >
                  <Add size={18} />
                  Onboard Agency
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Listing Content Table Card (Matches UserManagement.js) */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <Loader message="Loading healthcare agencies..." className="min-h-[300px]" />
          ) : paginatedTenants.length === 0 ? (
            // Empty state
            <div className="min-h-[300px] flex flex-col items-center justify-center gap-4 text-center p-8">
              <Global size={48} className="mx-auto text-muted-foreground/60" />
              <h4 className="text-lg font-bold text-foreground">No Agencies Found</h4>
              <p className="text-sm text-muted-foreground">
                {isFiltered 
                  ? "Try adjusting your search criteria or filters to locate the record." 
                  : "Onboard your first healthcare agency to get started."}
              </p>
              {isFiltered && (
                <Button variant="outline" onClick={handleClearFilters} className="mt-2">
                  Reset Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center font-semibold">Agency Name</TableHead>
                      <TableHead className="text-center font-semibold">Domain</TableHead>
                      <TableHead className="text-center font-semibold">Status</TableHead>
                      <TableHead className="text-center font-semibold">Admin Email</TableHead>
                      <TableHead className="text-center font-semibold">Created Date</TableHead>
                      <TableHead className="text-center font-semibold pr-6">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTenants.map((tenant) => (
                      <TableRow key={tenant._id || tenant.id} className="hover:bg-muted/50 transition-colors border-b last:border-0">
                        {/* Name */}
                        <TableCell className="text-center font-medium text-foreground py-4">
                          {tenant.name}
                        </TableCell>

                        {/* Domain */}
                        <TableCell className="text-center py-4">
                          <div className="flex items-center justify-center gap-1.5 text-muted-foreground">
                            <Global size={16} className="text-muted-foreground/80 shrink-0" />
                            <span className="truncate max-w-[200px]" title={tenant.domain}>
                              {tenant.domain}
                            </span>
                          </div>
                        </TableCell>

                        {/* Status */}
                        <TableCell className="text-center py-4">
                          {getStatusBadge(tenant.status)}
                        </TableCell>

                        {/* Contact Email */}
                        <TableCell className="text-center py-4">
                          <div className="flex items-center justify-center gap-1.5 text-muted-foreground">
                            <Sms size={16} className="text-muted-foreground/80 shrink-0" />
                            <a 
                              href={`mailto:${tenant.contact?.email}`}
                              className="hover:underline hover:text-foreground truncate max-w-[250px]"
                              title={tenant.contact?.email}
                            >
                              {tenant.contact?.email}
                            </a>
                          </div>
                        </TableCell>

                        {/* Created At */}
                        <TableCell className="text-center text-muted-foreground py-4">
                          <div className="flex items-center justify-center gap-1.5">
                            <Calendar size={16} className="text-muted-foreground/80 shrink-0" />
                            <span>
                              {tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-center py-4 pr-6">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/tenants/${tenant._id || tenant.id}`)}
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg"
                                >
                                  <Eye size={16} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Details</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Custom Pagination (Matches UserManagement.js) */}
              {totalItems > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t gap-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select
                      value={String(rowsPerPage)}
                      onValueChange={(val) => {
                        setRowsPerPage(Number(val));
                        setPage(0);
                      }}
                    >
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue placeholder={String(rowsPerPage)} />
                      </SelectTrigger>
                      <SelectContent>
                        {[5, 10, 25].map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      Showing {totalItems > 0 ? page * rowsPerPage + 1 : 0}–{Math.min((page + 1) * rowsPerPage, totalItems)} of {totalItems}
                    </span>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * rowsPerPage >= totalItems}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantList;